// app/src/Join.jsx — accept a workplace invite.
//
// Reached via an invite link (#/join?code=XXX) or the "I have a join code" entry.
// The code is captured at boot into sessionStorage BEFORE any Cognito redirect
// (which strips the hash), then used here once signed in. We ask the invitee for
// their name + language (the API is access-token-authorized, which carries no
// email/name — so the client must supply the display name) and pass them to the
// accept endpoint.

function Join({ code: codeProp }) {
  const initialCode = codeProp || sessionStorage.getItem('crew.invite') || readCodeFromHash() || '';
  const [code, setCode] = React.useState(initialCode);
  const [name, setName] = React.useState('');
  const [lang, setLang] = window.useLang();
  const [status, setStatus] = React.useState('form'); // form | joining | error
  const [err, setErr] = React.useState(null);

  // Prefill the name from the signed-in identity (ID-token claims have name/email).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await window.crew.auth.currentClaims();
        if (cancelled || !c) return;
        const guess = c.name || c.given_name || (c.email ? c.email.split('@')[0] : '');
        if (guess) setName((n) => n || guess);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const join = async () => {
    const c = (code || '').trim();
    const displayName = (name || '').trim();
    if (!c || !displayName) return;
    setStatus('joining'); setErr(null);
    try {
      const res = await window.crew.api(`/invitations/${encodeURIComponent(c)}/accept`, {
        method: 'POST', body: { displayName, preferredLang: lang },
      });
      sessionStorage.removeItem('crew.invite');
      window.crew.state.activeWorkplaceId = res.workplaceId;
      window.location.hash = '/board';
      window.location.reload();
    } catch (e) {
      setErr((e && e.message) || t('join.error', 'Could not join — the link may be invalid or expired.'));
      setStatus('error');
    }
  };

  const field = {
    width: '100%', padding: '12px 14px', marginBottom: 14, borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, boxSizing: 'border-box',
  };
  const label = { fontSize: 12, fontWeight: 700, color: 'var(--on-soft)', marginBottom: 6 };

  return (
    <div className="crew" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg)' }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <img src="/icons/kanjo-crew-app-icon.svg" alt="" style={{ width: 48, height: 48, borderRadius: 14, display: 'block', marginBottom: 18 }} />
        <h1 style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 24, color: 'var(--on)', margin: '0 0 6px' }}>
          {t('join.title', 'Join your team')}
        </h1>
        <p style={{ color: 'var(--on-soft)', fontSize: 14, margin: '0 0 22px' }}>
          {t('join.subtitle', 'Tell your crew who you are.')}
        </p>

        {!initialCode && (
          <div>
            <div style={label}>{t('join.inviteCode', 'Invite code')}</div>
            <input style={field} value={code} onChange={(e) => setCode(e.target.value.trim())} placeholder={t('join.inviteCodePlaceholder', 'Paste your invite code')} />
          </div>
        )}

        <div style={label}>{t('onboard.yourName', 'Your name')}</div>
        <input style={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Carlos" autoFocus />

        <div style={label}>{t('common.yourLanguage', 'Your language')}</div>
        <select style={field} value={lang} onChange={(e) => setLang(e.target.value)}>
          {window.CREW_LANGS.map((l) => <option key={l} value={l}>{window.langName(l)}</option>)}
        </select>

        {err && <div style={{ color: 'var(--primary)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        <button onClick={join} disabled={status === 'joining' || !name.trim() || !code.trim()} style={{
          width: '100%', padding: '13px 16px', cursor: 'pointer', borderRadius: 'var(--radius-sm)', border: 'none',
          background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15,
          opacity: (status === 'joining' || !name.trim() || !code.trim()) ? 0.6 : 1,
        }}>{status === 'joining' ? t('join.joining', 'Joining…') : t('join.join', 'Join')}</button>

        <button onClick={() => { sessionStorage.removeItem('crew.invite'); window.location.hash = '/board'; window.location.reload(); }} style={{
          width: '100%', marginTop: 10, padding: 10, border: 'none', background: 'none', color: 'var(--on-soft)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </div>
  );
}

function readCodeFromHash() {
  try {
    const m = (window.location.hash || '').match(/[?&]code=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}

Object.assign(window, { Join });
